import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Camera, Check, Clock, Crop, ImagePlus, Star, Trash2 } from 'lucide-react'
import type { PhotoInfo } from '../lib/api'
import { client } from '../lib/api'
import { capturePhotoFile } from '../lib/capturePhoto'
import { fileToObjectUrl, urlToObjectUrl } from '../lib/crop-image'
import { ensurePhotosPermission } from '../lib/nativePermissions'
import { PhotoCropModal } from './PhotoCropModal'

type ProfilePhotoGridProps = {
  photos: PhotoInfo[]
  /** Minimum empty slots to hint at (onboarding uses 3). */
  minSlots?: number
  maxPhotos?: number
  busy?: boolean
  uploadLabel?: string
  hint?: string
  manageable?: boolean
  onUpload: (file: File) => Promise<void>
  onReplace?: (photoId: string, file: File) => Promise<void>
  onReorder?: (photoIds: string[]) => Promise<void>
  onDelete?: (photoId: string) => Promise<void>
  onNotify?: (msg: string) => void
}

type CropSession = {
  src: string
  replacePhotoId?: string
  revoke?: boolean
}

function sortPhotos(photos: PhotoInfo[]): PhotoInfo[] {
  return [...photos].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

export function ProfilePhotoGrid({
  photos,
  minSlots = 3,
  maxPhotos = 9,
  busy = false,
  uploadLabel = 'Upload photo',
  hint,
  manageable = false,
  onNotify,
  onUpload,
  onReplace,
  onReorder,
  onDelete,
}: ProfilePhotoGridProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const replaceTargetRef = useRef<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [acting, setActing] = useState(false)
  const [cropSession, setCropSession] = useState<CropSession | null>(null)

  const sorted = useMemo(() => sortPhotos(photos), [photos])
  const selectedIndex = selectedId ? sorted.findIndex((p) => p.id === selectedId) : -1
  const selected = selectedIndex >= 0 ? sorted[selectedIndex] : null
  const canAdd = sorted.length < maxPhotos
  const disabled = busy || acting

  useEffect(() => {
    return () => {
      if (cropSession?.revoke) URL.revokeObjectURL(cropSession.src)
    }
  }, [cropSession])

  const slotCount = manageable
    ? sorted.length + (canAdd ? 1 : 0)
    : Math.min(maxPhotos, Math.max(minSlots, sorted.length + (canAdd ? 1 : 0)))

  const openCrop = async (file: File, replacePhotoId?: string) => {
    try {
      const src = await fileToObjectUrl(file)
      setCropSession({ src, replacePhotoId, revoke: true })
    } catch (e) {
      onNotify?.((e as Error).message)
    }
  }

  const openEditCrop = async () => {
    if (!selected?.urlPath) return
    setActing(true)
    try {
      const src = await urlToObjectUrl(selected.urlPath)
      setCropSession({ src, replacePhotoId: selected.id, revoke: true })
    } catch (e) {
      onNotify?.((e as Error).message)
    } finally {
      setActing(false)
    }
  }

  const closeCrop = () => {
    setCropSession((prev) => {
      if (prev?.revoke) URL.revokeObjectURL(prev.src)
      return null
    })
  }

  const pickPhoto = async () => {
    if (disabled || !canAdd) return
    const ok = await ensurePhotosPermission()
    if (!ok) {
      onNotify?.('Allow photo access in Settings to upload profile pictures')
      return
    }
    fileRef.current?.click()
  }

  const pickReplacement = async () => {
    if (disabled || !selected) return
    const ok = await ensurePhotosPermission()
    if (!ok) {
      onNotify?.('Allow photo access in Settings to upload profile pictures')
      return
    }
    replaceTargetRef.current = selected.id
    fileRef.current?.click()
  }

  const takePhoto = async (replace = false) => {
    if (disabled) return
    if (replace) {
      if (!selected) return
      replaceTargetRef.current = selected.id
    } else if (!canAdd) {
      return
    }
    setActing(true)
    try {
      const file = await capturePhotoFile({ facing: 'rear' })
      const replaceId = replaceTargetRef.current
      replaceTargetRef.current = null
      await openCrop(file, replaceId ?? undefined)
    } catch (e) {
      replaceTargetRef.current = null
      onNotify?.((e as Error).message)
    } finally {
      setActing(false)
    }
  }

  const handleFile = async (file: File | undefined) => {
    if (!file) return
    const replaceId = replaceTargetRef.current
    replaceTargetRef.current = null
    await openCrop(file, replaceId ?? undefined)
  }

  const confirmCrop = async (file: File) => {
    if (cropSession?.replacePhotoId) {
      if (onReplace) {
        await onReplace(cropSession.replacePhotoId, file)
      } else {
        await client.replacePhoto(cropSession.replacePhotoId, file)
        onNotify?.('Photo updated — pending review')
      }
    } else {
      await onUpload(file)
    }
    closeCrop()
  }

  const runAction = async (fn: () => Promise<void>) => {
    setActing(true)
    try {
      await fn()
    } finally {
      setActing(false)
    }
  }

  const reorder = async (photoIds: string[]) => {
    if (!onReorder) return
    await runAction(async () => {
      await onReorder(photoIds)
    })
  }

  const setPrimary = async () => {
    if (!selected || selectedIndex <= 0 || !onReorder) return
    const ids = sorted.map((p) => p.id)
    ids.splice(selectedIndex, 1)
    ids.unshift(selected.id)
    await reorder(ids)
    onNotify?.('Profile photo updated')
  }

  const moveEarlier = async () => {
    if (!selected || selectedIndex <= 0 || !onReorder) return
    const ids = sorted.map((p) => p.id)
    ;[ids[selectedIndex - 1], ids[selectedIndex]] = [ids[selectedIndex], ids[selectedIndex - 1]]
    await reorder(ids)
  }

  const moveLater = async () => {
    if (!selected || selectedIndex < 0 || selectedIndex >= sorted.length - 1 || !onReorder) return
    const ids = sorted.map((p) => p.id)
    ;[ids[selectedIndex], ids[selectedIndex + 1]] = [ids[selectedIndex + 1], ids[selectedIndex]]
    await reorder(ids)
  }

  const deleteSelected = async () => {
    if (!selected || !onDelete) return
    if (!window.confirm('Delete this photo?')) return
    await runAction(async () => {
      await onDelete(selected.id)
      setSelectedId(null)
    })
  }

  const renderSlot = (photo: PhotoInfo | undefined, index: number, empty: boolean) => {
    const pending = photo?.moderationStatus === 'pending'
    const isPrimary = !!photo && index === 0
    const isSelected = !!photo && photo.id === selectedId

    return (
      <button
        key={photo?.id ?? `slot-${index}`}
        type="button"
        className={`onboarding-photo-slot ${photo ? 'filled' : ''} ${empty ? 'clickable' : ''} ${isSelected ? 'selected' : ''}`}
        disabled={(!empty && !manageable) || disabled || (!empty && !photo)}
        aria-label={
          photo
            ? isPrimary
              ? `Profile photo ${index + 1}${isSelected ? ', selected' : ''}`
              : `Photo ${index + 1}${isSelected ? ', selected' : ''}`
            : `Add photo slot ${index + 1}`
        }
        aria-pressed={manageable && photo ? isSelected : undefined}
        onClick={() => {
          if (empty) {
            void pickPhoto()
            return
          }
          if (manageable && photo) {
            setSelectedId((id) => (id === photo.id ? null : photo.id))
          }
        }}
      >
        {photo?.urlPath ? <img src={photo.urlPath} alt="" /> : <span>{index + 1}</span>}
        {photo && isPrimary && <span className="photo-primary-badge">Profile</span>}
        {photo && (
          <i className={`onboarding-photo-check ${pending ? 'pending' : ''}`} aria-hidden>
            {pending ? <Clock size={12} /> : <Check size={14} />}
          </i>
        )}
      </button>
    )
  }

  return (
    <>
      <div className={`onboarding-photo-grid ${manageable ? 'manageable' : ''}`}>
        {manageable ? (
          <>
            {sorted.map((photo, i) => renderSlot(photo, i, false))}
            {canAdd && renderSlot(undefined, sorted.length, true)}
          </>
        ) : (
          Array.from({ length: slotCount }).map((_, i) => renderSlot(sorted[i], i, !sorted[i]))
        )}
      </div>

      {manageable && selected && (
        <div className="photo-manage-bar" role="toolbar" aria-label="Photo actions">
          <button
            type="button"
            className="button secondary photo-manage-btn"
            disabled={disabled}
            onClick={() => void pickReplacement()}
          >
            <ImagePlus size={14} /> Replace
          </button>
          <button
            type="button"
            className="button secondary photo-manage-btn"
            disabled={disabled}
            onClick={() => void takePhoto(true)}
          >
            <Camera size={14} /> Take photo
          </button>
          <button
            type="button"
            className="button secondary photo-manage-btn"
            disabled={disabled || !selected.urlPath}
            onClick={() => void openEditCrop()}
          >
            <Crop size={14} /> Edit crop
          </button>
          <button
            type="button"
            className="button secondary photo-manage-btn"
            disabled={disabled || selectedIndex <= 0}
            onClick={() => void setPrimary()}
          >
            <Star size={14} /> Set as profile
          </button>
          <button
            type="button"
            className="button secondary photo-manage-btn"
            disabled={disabled || selectedIndex <= 0}
            onClick={() => void moveEarlier()}
          >
            <ArrowLeft size={14} /> Earlier
          </button>
          <button
            type="button"
            className="button secondary photo-manage-btn"
            disabled={disabled || selectedIndex >= sorted.length - 1}
            onClick={() => void moveLater()}
          >
            Later <ArrowRight size={14} />
          </button>
          <button
            type="button"
            className="button secondary photo-manage-btn danger"
            disabled={disabled || !onDelete}
            onClick={() => void deleteSelected()}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          void handleFile(f)
          e.target.value = ''
        }}
      />
      <div className="onboarding-actions">
        <button type="button" className="button secondary" disabled={disabled || !canAdd} onClick={() => void pickPhoto()}>
          <ImagePlus size={16} /> {canAdd ? uploadLabel : 'Maximum photos'}
        </button>
        <button type="button" className="button secondary" disabled={disabled || !canAdd} onClick={() => void takePhoto(false)}>
          <Camera size={16} /> Take photo
        </button>
      </div>
      {manageable && !selected && sorted.length > 0 && (
        <p className="field-hint">Tap a photo to replace, take a new shot, crop, reorder, set your profile picture, or delete.</p>
      )}
      {!manageable && sorted.length > 0 && (
        <p className="field-hint">Every upload opens the crop tool — drag and zoom to frame your photo.</p>
      )}
      {hint && <p className="field-hint">{hint}</p>}

      {cropSession && (
        <PhotoCropModal
          imageSrc={cropSession.src}
          title={cropSession.replacePhotoId ? 'Edit photo crop' : 'Crop photo'}
          onCancel={closeCrop}
          onConfirm={confirmCrop}
        />
      )}
    </>
  )
}
